import { Component, computed, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { finalize, Subject, debounceTime, takeUntil } from 'rxjs';
import { Business, BusinessEntityType } from '../../models/finance/business';
import { BusinessProxyService } from '../../proxies/finance/business.proxy.service';
import { ModalService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { BusinessLotNumbersModalComponent } from './business-lot-numbers-modal.component';
import { Popover } from 'primeng/popover';
import { UserGroup } from '@app/models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-business-lot-numbers',
  templateUrl: './business-lot-numbers.component.html',
  styleUrl: './business-lot-numbers.component.scss',
})
export class BusinessLotNumbersComponent implements OnInit, OnDestroy {
  @ViewChild('lotOverflow') lotOverflow!: Popover;

  isLoading = signal<boolean>(false);
  businesses = signal<Business[]>([]);
  totalRecords = signal<number>(0);
  nameFilter = signal<string>('');
  lotNumberFilter = signal<string>('');
  overflowLotNumbers = signal<string[]>([]);

  filteredBusinesses = computed(() => {
    const filter = this.lotNumberFilter().trim().toLowerCase();
    if (!filter) return this.businesses();
    return this.businesses().filter((b) => b.lotNumbers?.some((l) => l.toLowerCase().includes(filter)));
  });

  readonly pageSize = 25;
  currentPage = 1;

  /** Show 7 chips; the 8th slot becomes "+N more" when overflow exists */
  readonly previewCount = 7;

  readonly businessTypeLabels: Record<number, string> = {
    [BusinessEntityType.Provider]: 'Provider',
    [BusinessEntityType.Client]: 'Client',
    [BusinessEntityType.Manufacturer]: 'Manufacturer',
    [BusinessEntityType.Consignee]: 'Consignee',
    [BusinessEntityType.Brand]: 'Brand',
    [BusinessEntityType.InternalCompany]: 'Internal Company',
  };

  private readonly nameSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private businessProxyService: BusinessProxyService,
    private modalService: ModalService,
  ) {}

  canEdit() {
    // if Admin or Editor
    if (AuthService.hasRole(UserGroup.BusinessLotNumbersAdmin)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(UserGroup.BusinessLotNumbersViewer)) {
      return false;
    }
    // Default
    return false;
  }

  ngOnInit(): void {
    this.nameSearch$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe((name) => {
      this.loadBusinesses(1, name);
    });
    this.loadBusinesses();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadBusinesses(page = 1, name?: string): void {
    this.currentPage = page;
    this.isLoading.set(true);

    this.businessProxyService
      .search(
        {
          name: name !== undefined ? name : this.nameFilter() || undefined,
          businessTypes: [BusinessEntityType.Manufacturer],
        },
        page,
        this.pageSize,
        'name',
        1,
      )
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.businesses.set(response.data.items ?? []);
            this.totalRecords.set(response.data.totalCount ?? 0);
          }
        },
      });
  }

  onNameFilterChange(value: string): void {
    this.nameFilter.set(value);
    this.nameSearch$.next(value);
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const page = Math.floor((event.first ?? 0) / (event.rows ?? this.pageSize)) + 1;
    this.loadBusinesses(page);
  }

  openModal(business: Business): void {
    this.modalService.open(BusinessLotNumbersModalComponent, {}, { business: structuredClone(business) }).subscribe((result) => {
      if (result?.action === 'save' && result.data != null) {
        const updated = result.data;
        this.businesses.set(this.businesses().map((b) => (b.id === updated.id ? updated : b)));
      }
    });
  }

  getPreviewLotNumbers(business: Business): string[] {
    const lots = business.lotNumbers ?? [];
    return lots.length > this.previewCount ? lots.slice(0, this.previewCount) : lots;
  }

  getExtraCount(business: Business): number {
    return Math.max(0, (business.lotNumbers?.length ?? 0) - this.previewCount);
  }

  showOverflow(event: Event, business: Business): void {
    this.overflowLotNumbers.set((business.lotNumbers ?? []).slice(this.previewCount));
    this.lotOverflow.toggle(event);
  }
}
